import { z } from "zod";

import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { ExternalLink } from "@/components/ExternalLink";
import { postAuthorized } from "@/lib/fetch";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCcw } from "lucide-react";

const FormSchema = z.object({
  protocol: z.string().nullable().optional(),
  connector: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  filename: z.string().nullable().optional(),
  connector_config: z
    .union([
      z.string().nullable().optional(),
      z.object({
        id: z.number(),
        text: z.string().nullable().optional(),
      }),
    ])
    .nullable()
    .optional(),
  connector_config_id: z.coerce.number().nullable().optional(),
  unique_attribute_id: z.string().nullable().optional(),
  // allow both in form state
  gmail_labels: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
});

const normalizeGmailLabels = (
  value: string | string[] | null | undefined,
): string[] => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((label) => label.trim().toUpperCase())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.map((label) => label.trim().toUpperCase()).filter(Boolean);
  }

  return [];
};

export function InputFilesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language?.startsWith("fr");

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "connector_config") {
        return {
          id: fetchedData.connector_config_id,
          text: fetchedData.connector_config,
        };
      }
      return value;
    },
  });

  const handleSubmitFixed = form.handleSubmit((values) => {
    onSubmit({
      ...values,
      gmail_labels: normalizeGmailLabels(values.gmail_labels),
    });
  });

  const dispatchMutation = useMutation({
    mutationFn: async () =>
      postAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/${id}/dispatch`,
        {},
      ),
    onSuccess: () => {
      toast.success(
        isFrench
          ? "Le dispatcher a été lancé."
          : "Dispatcher launched successfully.",
      );
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        (isFrench
          ? "Impossible de lancer le dispatcher."
          : "Failed to launch dispatcher.");
      toast.error(message);
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={handleSubmitFixed}>
              <CardHeader className="text-lg font-bold text-gray-700">
                InputFiles {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="protocol"
                    label={t(`${table_name}.columns.protocol`, {
                      ns: "tables",
                      defaultValue: "Protocol",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="connector"
                    label={t(`${table_name}.columns.connector`, {
                      ns: "tables",
                      defaultValue: "Connector",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="connector_config"
                    label="Connector Config"
                    base_url="etl-quantilia/connector_configs"
                    column_name="path"
                    id_column="connector_config_id"
                    control={form.control}
                    options={{
                      itemLink:
                        form.watch("connector_config_id") != null
                          ? toWorkspacePath(
                              `/admin/qtl - etl/connector_configs/${form.watch("connector_config_id")}`,
                            )
                          : undefined,
                    }}
                  />
                  <InputField
                    name="path"
                    label={t(`${table_name}.columns.path`, {
                      ns: "tables",
                      defaultValue: "Path",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="filename"
                    label={t(`${table_name}.columns.filename`, {
                      ns: "tables",
                      defaultValue: "Filename",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unique_attribute_id"
                    label={t(`${table_name}.columns.unique_attribute_id`, {
                      ns: "tables",
                      defaultValue: "Unique Attribute ID",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="gmail_labels"
                    label={t(`${table_name}.columns.gmail_labels`, {
                      ns: "tables",
                      defaultValue: "Gmail Labels",
                    })}
                    control={form.control}
                    capitalize
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["connector_config"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>

        <div className="absolute top-[3.20rem] right-12 2xl:right-10 hidden lg:flex flex-col items-end gap-4">
          <Button
            type="button"
            onPress={() => dispatchMutation.mutate()}
            isLoading={dispatchMutation.isPending}
            startContent={
              dispatchMutation.isPending ? null : (
                <RefreshCcw className="h-4 w-4" />
              )
            }
            radius="lg"
            variant="solid"
            color="primary"
            className="h-10 text-sm font-medium shadow-[0_1px_0_rgba(16,24,40,0.04)]"
          >
            {isFrench ? "Lancer le dispatcher" : "Launch dispatcher"}
          </Button>
          <div className="mt-2 flex flex-col items-end gap-4">
            {form.watch("connector_config_id") ? (
              <Button
                href={toWorkspacePath(
                  `/admin/qtl - etl/connector_configs/${form.watch(
                    "connector_config_id",
                  )}`,
                )}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                Connector Config
              </Button>
            ) : null}
            <Button
              href={toWorkspacePath(
                `/admin/qtl - etl/time_serie_files?input_file_id=${id}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Time Series Files
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
